import { useCallback, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';

// store
import { useStoreValue } from '../store';
import { queryKeys } from '../lib/query-client';

// service
import ProjectService from '../service/project-service';

// permissions
import { EMPTY_POLICY, PROJECT_ROLES, makeProjectPolicy } from '../helpers/projectRoles';

/**
 * Resolves the role the current user holds inside a project, and the policy
 * that follows from it.
 *
 * The API has no "my role here" endpoint: `/projects/{id}/team` lists the
 * invitations, each carrying a `ProjectTeamRole`, but the project owner is not
 * among them — they only appear as the `senderId` of the invitations. The
 * backend itself treats the owner as PRODUCER (that is the `authorRole` it
 * stamps on their messages), so the owner is resolved the same way here: a
 * member of the project with no invitation of their own is its producer.
 *
 * That inference only holds when the team could actually be read. A failed
 * request leaves the role unknown and the policy empty, so the UI shows no
 * action it cannot back up.
 *
 * @param   {string} projectId
 * @returns {{role: string|null, isOwner: boolean, can: object, team: array,
 *            isLoading: boolean, refreshRole: function}}
 */
const useProjectRole = (projectId) => {
    const queryClient = useQueryClient();
    const userData = useStoreValue('userData');
    const currentUserId = userData?.id || null;

    const { data: team = [], isLoading, isError } = useQuery({
        queryKey: queryKeys.projectTeam(projectId),
        queryFn: () => ProjectService.getProjectTeamAsync(projectId),
        enabled: Boolean(projectId),
    });

    // Called after a denied action: the role we assumed is stale.
    const refreshRole = useCallback(() => {
        if (!projectId) return;

        queryClient.invalidateQueries({ queryKey: queryKeys.projectTeam(projectId) });
    }, [projectId, queryClient]);

    return useMemo(() => {
        if (!projectId || !currentUserId || isLoading || isError) {
            return { role: null, isOwner: false, can: EMPTY_POLICY, team: [], isLoading, refreshRole };
        }

        const mine = (team || []).find(member => member.userId === currentUserId);
        const isOwner = !mine;
        const role = mine?.role || (isOwner ? PROJECT_ROLES.PRODUCER : null);

        return {
            role,
            isOwner,
            can: makeProjectPolicy({ role, currentUserId }),
            team,
            isLoading,
            refreshRole,
        };
    }, [projectId, currentUserId, team, isLoading, isError, refreshRole]);
}

export default useProjectRole;
